<template>
  <motlet :options="motletOptions">
    <template #content>
      <!-- eslint-disable-next-line prettier/prettier -->
      <scatter
        :system="system"
        :items="nodes"
        :values="values"
        :y-axis-max="yAxisMax"
        :selected-handler="selectedHandler"
      />
    </template>
  </motlet>
</template>

<script>
import { format, parse } from 'date-fns';
import { zonedTimeToUtc } from 'date-fns-tz';
import instanceable from './mixins/instanceable';
import Scatter from '../chart/Scatter.vue';

export default {
  mixins: [instanceable],
  components: {
    Scatter,
  },
  data() {
    return {
      motletOptions: {
        id: 'overview/system/java/OwlView',
        title: 'OWL View',
      },
      messageNames: ['rawJavaWorkload.3sec'],
      messageFields: ['stime', 'owlviewData', 'starttime'],
      nodeSequence: 0,
      value: {},
      values: [],
      // y 좌표 최대값
      yAxisMax: 6000,
      // chart 선택 handler
      selectedHandler: p => {
        const query = {
          sdt: p.dates[0],
          edt: p.dates[1],
          stack: p.system.stackType,
          sid: p.system.id,
          min: p.values[0],
          max: p.values[1],
        };
        // 그래프에 표시된 값은 6000 값을 최대값으로 하여 보여주고 있으나,
        // 실제 DB 값은 10000 값을 최대값으로 하여 기록되고 있다.
        // 이렇게 임의로 지정된 최대값으로 해당 건을 조회하려고 하면 실제 값의 범위에 들지 않으므로, 조회가 불가능하다.
        // 따라서 실제의 값을 알 수 없는 상태에서 조회하기 위해 값 범위의 최대값을 무한정 큰 값을 지정할 수 없으므로,
        // 의미상으로 DB 최대값으로 지정된 수치 10000 일 경우 최대값은 빈 값으로 지정하여 조회한다.
        if (query.max === 10000) delete query.max;
        window.open(`/views/analysis/owlview?${new URLSearchParams(query).toString()}`, '_blank');
      },
    };
  },
  watch: {
    nodes: {
      handler: 'changeNodesHandler',
    },
  },
  methods: {
    wsBufferedDataHandler(key, responses) {
      // 1. 그룹 별 첫번째 message 수신의 경우 값 초기화
      if (key.name == this.messageNames[0]) this.value[key.nodeId] = [];

      // 2. 그룹별 데이터 누적처리
      responses.forEach(response => response.forEach(rs => this.dataHandler(key, rs.data)));

      // 3. 그룹 별 마지막 message 수신의 경우 처리
      if (key.name == this.messageNames[this.messageNames.length - 1]) this.finalizeGroupData(key);
    },
    wsDataHandler(key, response) {
      // 1. 그룹 별 첫번째 message 수신의 경우 값 초기화
      if (key.name == this.messageNames[0]) this.value[key.nodeId] = [];

      // 2. 그룹별 데이터 누적처리
      // if (!empty) this.dataHandler(key, response);
      this.dataHandler(key, response);

      // 3. 그룹 별 마지막 message 수신의 경우 처리
      if (key.name == this.messageNames[this.messageNames.length - 1]) this.finalizeGroupData();
    },
    // 개별 데이터 처리
    dataHandler(key, response) {
      const mscounts = res => {
        // 수집된 데이터가 없을 경우 -1 값으로 지정하며, chart 에서는 제외한다.
        // 따라서 x좌표(시간)값은 표시되며, y좌표(responsetime) 은 표시되지 않음.
        return ((res || {}).owlviewData || '').split(';').map(data => (!data ? '-1=0' : data));
      };
      response.forEach(res => {
        const timestamp = zonedTimeToUtc(format(parse(res.starttime, 'yyyyMMddHHmmss', new Date()), 'yyyy-MM-dd HH:mm:ss.SSS'), { timeZone: this.$store.state.common.user.timeZoneId }).getTime();
        mscounts(res).forEach(mscount => {
          const countInfo = mscount.split('=');
          const responseTime = parseInt(countInfo[0]);
          const responseCount = parseInt(countInfo[1]);
          const yAxisValue = responseTime > this.yAxisMax ? this.yAxisMax : responseTime; // 기준치 초과 값은 지정된 최대값으로 표시한다.
          // 한개의 포인트만 표시하도록 중복 제거함.
          // 단, response time 에 해당하는 count는 각 포인트의 합으로 계산함.
          const target = this.value[key.nodeId].find(item => item.starttime == timestamp && item.yAxisValue == yAxisValue);
          if (target) {
            target.responseCount += responseCount;
            target.responseTime = responseTime;
          } else {
            this.value[key.nodeId].push({
              id: key.nodeId,
              yAxisValue,
              responseTime,
              responseCount,
              timestamp,
            });
          }
        });
      });
    },
    // 누적 값 최종 처리
    finalizeGroupData() {
      ++this.nodeSequence;
      // 인스턴스들의 값이 모두 취합 된 후
      if (this.nodeSequence == this.nodes.length) {
        this.values = this.nodes.map(node => this.value[node.id] || []);
        this.nodeSequence = 0;
      }
    },
    subscribe() {
      this.$websocket.realtime.subscribe({
        requests: this.nodes.reduce(
          (acc, node) =>
            acc.concat(
              this.messageNames.map(name => ({
                key: { name: name, sstmId: node.systemId, instId: node.instanceId, nodeId: node.id },
                fields: this.messageFields,
                handler: {
                  buffer: this.wsBufferedDataHandler,
                  every: this.wsDataHandler,
                },
              })),
            ),
          [],
        ),
      });
    },
    initialize() {
      this.nodeSequence = 0;
      // websoket message init
      this.wsRequestMessages = {};
    },
    changeNodesHandler() {
      this.initialize();
      this.subscribe();
    },
  },
};
</script>
